import type { ICriticalReadingData } from './types';
import type { TFormItem } from '../types';

const isEmpty = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/**
 * Walk a step's TFormItem[] and collect the `name`s of required fields left empty.
 * Conditional sub-fields (subItems under a selectable option) are only validated
 * when their parent option is the selected value — mirrors what the renderer shows.
 */
export const validateStepItems = (
  items: TFormItem[],
  values: ICriticalReadingData,
): string[] => {
  const errors: string[] = [];

  const walk = (list: TFormItem[]) => {
    list.forEach((item) => {
      if (item.type === 'info' || item.type === 'empty') {
        if ('items' in item && item.items) walk(item.items);
        return;
      }

      if (item.required && isEmpty(values[item.name])) {
        errors.push(item.name);
      }

      // nested plain items
      if ('items' in item && item.items) walk(item.items);

      // options' conditional subItems: validate only the selected option's subItems
      if ('options' in item && item.options) {
        item.options.forEach((opt) => {
          if (!opt.subItems) return;
          const isSelected = values[item.name] === opt.value;
          if (isSelected) walk(opt.subItems);
        });
      }
    });
  };

  walk(items);
  return errors;
};

/**
 * Validate the symptoms step (custom-rendered, not a TFormItem[]): every displayed
 * sym_* radio plus the Other radio must be answered.
 */
export const validateSymptoms = (
  symptomNames: string[],
  values: ICriticalReadingData,
): string[] => {
  const errors = symptomNames.filter((name) => isEmpty(values[name]));
  if (isEmpty(values.symptomsOther)) errors.push('symptomsOther');
  if (values.symptomsOther === 'yes' && isEmpty(values.symptomsOtherText)) {
    errors.push('symptomsOtherText');
  }
  return errors;
};
